import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Users, ArrowRight, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { admin: 'Admin', hr: 'HR Manager', employee: 'Employee', public_user: 'Public Viewer' };

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setError('Invalid invitation link'); setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/team/invite/${token}`);
        setInvite(res.data);
        setFirstName(res.data.first_name || '');
        setLastName(res.data.last_name || '');
      } catch (err) {
        setError(err.response?.data?.detail || 'This invitation is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/team/accept-invite`, { token, password, first_name: firstName, last_name: lastName });
      localStorage.setItem('paf_token', res.data.token);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF] px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-[440px]">
        <Link to="/" className="flex items-center gap-3 mb-10 group w-fit">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-[#0F172A] text-xl font-bold tracking-tight">PAFHub</span>
        </Link>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-3" />
              <p className="text-sm text-[#64748B]">Loading invitation...</p>
            </div>
          ) : error && !invite ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-red-500" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-2">Invalid Invitation</h2>
              <p className="text-sm text-[#64748B] mb-6">{error}</p>
              <Link to="/login" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium hover:underline">Go to Sign In</Link>
            </div>
          ) : success ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-2xl bg-green-50 border border-green-100 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-green-600" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-2">Welcome to the team!</h2>
              <p className="text-sm text-[#64748B]">Redirecting to your dashboard...</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
                </div>
              </div>
              <h2 data-testid="accept-invite-title" className="text-xl font-bold text-[#0F172A] text-center mb-1">Join the team</h2>
              <p className="text-sm text-[#64748B] text-center mb-2">
                {invite.invited_by_name ? `${invite.invited_by_name} invited you` : "You've been invited"} as
              </p>
              <p className="text-center mb-6">
                <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
                  {ROLE_LABELS[invite.role] || invite.role}
                </span>
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl">{error}</div>
              )}

              <form onSubmit={handleAccept} className="space-y-4">
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Email</Label>
                  <Input value={invite.email} disabled className="rounded-xl border-[#E2E8F0] bg-slate-50 h-11 shadow-sm text-[#64748B]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">First Name</Label>
                    <Input data-testid="accept-first-name" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                  </div>
                  <div>
                    <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Last Name</Label>
                    <Input data-testid="accept-last-name" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Password</Label>
                  <Input data-testid="accept-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a password" required minLength={6} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div>
                  <Label className="text-[#0F172A] text-sm font-medium mb-1.5 block">Confirm Password</Label>
                  <Input data-testid="accept-confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" required minLength={6} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <Button
                  data-testid="accept-invite-btn"
                  type="submit"
                  disabled={accepting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 font-semibold text-[15px] shadow-md hover:shadow-lg hover:shadow-indigo-500/25 active:scale-[0.98] transition-all"
                >
                  {accepting ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="flex items-center gap-2">Accept & Join <ArrowRight className="w-4 h-4" /></span>}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
